import { useDataProvider, useGetList } from "ra-core";
import { useRef, useState } from "react";
import { Link } from "react-router";
import { Send, Sparkles } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import {
  useConfigurationContext,
  useConfigurationUpdater,
} from "../root/ConfigurationContext";
import { callByoAi } from "./byoai";
import { AI_TOOLS, executeAiTool } from "./tools";
import type { CrmDataProvider } from "../providers/types";
import type { ConfigurationContextValue } from "../root/ConfigurationContext";
import type { Contact, Deal } from "../types";

/**
 * Ask AI - bring your own key. Point Obvious at an Anthropic or OpenAI key
 * under Integrations and this panel answers questions with your CRM context
 * in hand (pipeline, hot contacts, recent activity), and can create
 * contacts/companies/deals/tasks on request (decision #51) - each one a
 * plain dataProvider.create(), same as the app's own "New X" forms - plus
 * linear (no-branching) automation workflows (decision #59), created
 * disabled so a person reviews one before it starts acting on real contacts.
 *
 * Anthropic keys work straight from the browser (their API allows
 * CORS-enabled direct access). OpenAI blocks browser calls, so those route
 * through the Obvious services proxy at deployment - the key is stored either
 * way and never leaves your tenant.
 */

interface Msg {
  role: "user" | "assistant";
  text: string;
}

const buildContext = (
  contacts: Contact[],
  deals: Deal[],
  config: ConfigurationContextValue,
): string => {
  const open = deals.filter(
    (d) => d.stage !== "won" && d.stage !== "lost" && !d.archived_at,
  );
  const won = deals.filter((d) => d.stage === "won");
  const openTotal = open.reduce((s, d) => s + (Number(d.amount) || 0), 0);
  const topDeals = [...open]
    .sort((a, b) => b.amount - a.amount)
    .slice(0, 8)
    .map((d) => `- ${d.name}: $${Math.round(d.amount).toLocaleString()} (${d.stage})`)
    .join("\n");
  const recent = [...contacts]
    .sort(
      (a, b) => new Date(b.last_seen).getTime() - new Date(a.last_seen).getTime(),
    )
    .slice(0, 8)
    .map((c) => `- ${c.first_name} ${c.last_name}, ${c.title ?? ""} (${c.status})`)
    .join("\n");
  const sequenceNames = (config.sequences ?? []).map((s) => s.name).join(", ");
  const templateNames = (config.emailTemplates ?? []).map((t) => t.name).join(", ");
  const stageNames = (config.dealStages ?? []).map((s) => s.label).join(", ");
  return [
    `You are Obvious's AI assistant inside a CRM. Answer concisely using this live CRM snapshot. When asked to add or log something, use the available tools to actually create it rather than explaining how. When asked to build a workflow, use create_workflow - it makes disabled linear (no-branching) workflows for a person to review; if they want branching, tell them to open Marketing → Workflows once it's created. When asked to build a follow-up cadence or drip, use create_sequence - email/wait/task steps run in order; it does nothing on its own until enrolled via a workflow's enroll_sequence action or manually from Marketing → Sequences. Today is ${new Date().toDateString()}.`,
    `CRM snapshot: ${contacts.length} contacts; ${open.length} open deals worth $${Math.round(openTotal).toLocaleString()}; ${won.length} closed-won.`,
    `For workflow actions: existing sequences: ${sequenceNames || "none yet"}. Existing email templates: ${templateNames || "none yet"}. Pipeline stages: ${stageNames || "none configured"}.`,
    `Top open deals:\n${topDeals}`,
    `Recently active contacts:\n${recent}`,
  ].join("\n\n");
};

export const AiAssistant = () => {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState<Msg[]>([]);
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const config = useConfigurationContext();
  const updateConfig = useConfigurationUpdater();
  const dataProvider = useDataProvider<CrmDataProvider>();

  const ai = config.integrations?.ai?.fields ?? {};
  const provider = ai.provider || "anthropic";
  const apiKey = (ai.apiKey ?? "").trim();

  const { data: contacts = [] } = useGetList<Contact>(
    "contacts",
    { pagination: { page: 1, perPage: 500 } },
    { enabled: open },
  );
  const { data: deals = [] } = useGetList<Deal>(
    "deals",
    { pagination: { page: 1, perPage: 1000 } },
    { enabled: open },
  );

  const push = (m: Msg) =>
    setMessages((prev) => {
      const next = [...prev, m];
      setTimeout(() => {
        scrollRef.current?.scrollTo({ top: 999999, behavior: "smooth" });
      }, 50);
      return next;
    });

  const send = async () => {
    const question = input.trim();
    if (!question || busy) return;
    setInput("");
    push({ role: "user", text: question });

    if (!apiKey) {
      push({
        role: "assistant",
        text: "No AI key connected yet. Add your Anthropic or OpenAI key under Integrations → AI, and I'll answer with your CRM data in hand.",
      });
      return;
    }

    setBusy(true);
    const system = buildContext(contacts, deals, config);
    const history = [...messages, { role: "user" as const, text: question }];

    const result = await callByoAi(config.integrations?.ai, system, history, {
      tools: AI_TOOLS,
      executeTool: (name, toolInput) =>
        executeAiTool(name, toolInput, { dataProvider, contacts, config, updateConfig }),
    });
    push({ role: "assistant", text: result.text });
    setBusy(false);
  };

  return (
    <>
      <button
        type="button"
        aria-label="Ask AI"
        onClick={() => setOpen(true)}
        className="fixed bottom-5 right-5 z-40 inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground shadow-lg px-4 py-2.5 text-sm font-medium hover:opacity-90 transition-opacity"
      >
        <Sparkles className="w-4 h-4" />
        Ask AI
      </button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md flex flex-col p-0">
          <SheetHeader className="px-4 pt-4 pb-2 border-b">
            <SheetTitle className="flex items-center gap-2 text-base">
              <Sparkles className="w-4 h-4" /> Ask AI
            </SheetTitle>
            <SheetDescription className="text-xs">
              {apiKey
                ? `Using your ${provider === "openai" ? "OpenAI" : "Anthropic"} key with live CRM context.`
                : (
                  <>
                    Bring your own key -{" "}
                    <Link to="/integrations" className="underline" onClick={() => setOpen(false)}>
                      connect it under Integrations
                    </Link>
                    .
                  </>
                )}
            </SheetDescription>
          </SheetHeader>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 flex flex-col gap-3">
            {messages.length === 0 && (
              <div className="text-sm text-muted-foreground flex flex-col gap-2">
                <p>Ask anything about your CRM:</p>
                {[
                  "Which deals should I focus on this week?",
                  "Summarize my pipeline by stage.",
                  "Who's gone quiet that I should follow up with?",
                ].map((s) => (
                  <button
                    key={s}
                    className="text-left rounded-md border border-border px-3 py-2 hover:border-primary/50 transition-colors"
                    onClick={() => setInput(s)}
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
            {messages.map((m, i) => (
              <div
                key={i}
                className={`rounded-lg px-3 py-2 text-sm whitespace-pre-wrap max-w-[85%] ${
                  m.role === "user"
                    ? "self-end bg-primary text-primary-foreground"
                    : "self-start bg-muted"
                }`}
              >
                {m.text}
              </div>
            ))}
            {busy && (
              <div className="self-start bg-muted rounded-lg px-3 py-2 text-sm text-muted-foreground">
                Thinking…
              </div>
            )}
          </div>

          <div className="border-t p-3 flex items-center gap-2">
            <Input
              placeholder="Ask about your pipeline, contacts, next steps…"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") send();
              }}
            />
            <Button size="icon" disabled={busy || !input.trim()} onClick={send} aria-label="Send">
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};
